/**
 * groupAmortizationSchedule
 *
 * Presentation-only grouping of a multi-investor amortization schedule. For
 * multi-investor contracts the schedule has one row per investor per period
 * (e.g. 42 periods x 2 investors = 84 rows); this collapses consecutive
 * same-period rows into one period group and SUMS the per-investor values —
 * it never recomputes or regenerates any amount, it only adds existing values.
 *
 * A period group is keyed by `payment_date|narration` and relies on same-period
 * investor rows being adjacent in the input (the DB stores them in that order).
 * Each group keeps its raw per-investor rows in `entries` so callers can render
 * an expandable per-investor breakdown.
 *
 * `outstanding_balance` is SUMMED like every other amount. The engine writes it
 * per investor tranche, never contract-level: generateMultiInvestorSchedule runs
 * the single-investor engine once per investor with that investor's own
 * capital_amount, so each row carries only that investor's balance. The
 * contract-level balance for a period is therefore the sum of its concurrent
 * tranches (2 investors x 100k each => two rows of 100 000, contract = 200 000).
 *
 * This is the single source of truth for the grouping, shared by
 * AmortizationScheduleTable (wizard / contract detail) and the approval modal,
 * so both present identical grouped totals (financial consistency).
 *
 * @param {Array} schedule - raw per-investor schedule rows
 * @returns {{ isMultiInvestor: boolean, groups: Array|null }}
 *   isMultiInvestor: true when any row carries an investor_id
 *   groups: the grouped periods, or null when not multi-investor
 */
export function groupAmortizationSchedule(schedule) {
  const rows = Array.isArray(schedule) ? schedule : [];
  const isMultiInvestor = rows.some((entry) => entry.investor_id != null);

  if (!isMultiInvestor || rows.length === 0) {
    return { isMultiInvestor, groups: null };
  }

  const groups = [];
  let currentGroup = null;

  rows.forEach((entry, index) => {
    const taggedEntry = { ...entry, _originalIndex: index };
    const groupKey = `${entry.payment_date}|${entry.narration}`;

    if (currentGroup && currentGroup.groupKey === groupKey) {
      currentGroup.entries.push(taggedEntry);
      currentGroup.capital_amount += parseFloat(entry.capital_amount) || 0;
      currentGroup.interest_amount += parseFloat(entry.interest_amount) || 0;
      const entryNet = parseFloat(entry.net_interest_amount);
      currentGroup.net_interest_amount += Number.isFinite(entryNet) ? entryNet : (parseFloat(entry.interest_amount) || 0);
      currentGroup.wht_amount += parseFloat(entry.wht_amount) || 0;
      currentGroup.payment_amount += parseFloat(entry.payment_amount) || 0;
      const entryBalance = parseFloat(entry.outstanding_balance);
      if (Number.isFinite(entryBalance)) currentGroup.outstanding_balance += entryBalance;
      currentGroup.lastEntryIndex = index;
      currentGroup.investorCount = currentGroup.entries.length;
      if (entry.has_override) currentGroup.hasAnyOverride = true;
      // Track if rates differ across investors
      const rate = parseFloat(entry.interest_rate);
      if (rate && rate !== currentGroup._firstRate) currentGroup._ratesDiffer = true;
      // Track if gross-up treatment differs across investors
      if (!!entry.wht_gross_up !== !!currentGroup.wht_gross_up) currentGroup._grossUpVaries = true;
      // Aggregate status: if any Pending, show Pending. T3-171: likewise a period where any investor
      // is still 'Settling' (payout / held reserve unconfirmed) must not read 'Paid' off another
      // investor's confirmed payout.
      if (entry.status === 'Pending') currentGroup.status = 'Pending';
      else if (entry.status === 'Settling' && currentGroup.status !== 'Pending') currentGroup.status = 'Settling';
    } else {
      currentGroup = {
        groupKey,
        payment_date: entry.payment_date,
        period_start: entry.period_start,
        period_end: entry.period_end,
        narration: entry.narration,
        version_number: entry.version_number,
        // T3-119: needed so the group header chip can render a terminal version as "VT".
        // Without it the chip falls back to V{n} and a terminated schedule looks like any other.
        version_type: entry.version_type,
        interest_rate: entry.interest_rate,
        // T3-170: benchmark + spread before the contract floor/ceiling, where a bound was binding.
        uncapped_interest_rate: entry.uncapped_interest_rate ?? null,
        _firstRate: parseFloat(entry.interest_rate) || 0,
        _ratesDiffer: false,
        days_in_period: entry.days_in_period,
        status: entry.status,
        capital_amount: parseFloat(entry.capital_amount) || 0,
        interest_amount: parseFloat(entry.interest_amount) || 0,
        net_interest_amount: (() => {
          const n = parseFloat(entry.net_interest_amount);
          return Number.isFinite(n) ? n : (parseFloat(entry.interest_amount) || 0);
        })(),
        wht_amount: parseFloat(entry.wht_amount) || 0,
        wht_gross_up: !!entry.wht_gross_up,
        _grossUpVaries: false,
        payment_amount: parseFloat(entry.payment_amount) || 0,
        // Sum of the period's per-investor tranche balances (see header comment).
        // Finite-guarded so a pre-T3-055 snapshot without the field yields 0, not NaN.
        outstanding_balance: (() => {
          const b = parseFloat(entry.outstanding_balance);
          return Number.isFinite(b) ? b : 0;
        })(),
        entries: [taggedEntry],
        lastEntryIndex: index,
        firstEntryIndex: index,
        investorCount: 1,
        hasAnyOverride: !!entry.has_override,
      };
      groups.push(currentGroup);
    }
  });

  return { isMultiInvestor, groups };
}

export default groupAmortizationSchedule;
